import React, { useState, useEffect } from 'react';
import { MIN_PASSWORD_LENGTH } from '../../lib/passwordPolicy';
import { Eye, EyeOff, Lock, Mail, AlertCircle, Shield, ShieldCheck } from 'lucide-react';

interface PasswordSetFormProps {
  onSubmit: (password: string, recoveryEmail?: string) => void;
  submitLabel?: string;
  loading?: boolean;
  showRecoveryEmail?: boolean;
}

export default function PasswordSetForm({
  onSubmit,
  submitLabel = "Submit",
  loading = false,
  showRecoveryEmail = false,
}: PasswordSetFormProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [strength, setStrength] = useState<'Weak' | 'Fair' | 'Good' | 'Strong'>('Weak');
  const [strengthScore, setStrengthScore] = useState(0); // 0-4

  // Touched states to only show non-matching feedback once user actually typed in both
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);

  // Evaluate password strength on every keystroke
  useEffect(() => {
    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    // Normalize score to 1-4 scale if password is non-empty
    if (password.length === 0) {
      setStrengthScore(0);
      setStrength('Weak');
      return;
    }

    const calculatedScore = Math.min(4, Math.max(1, score - (password.length < MIN_PASSWORD_LENGTH ? 1 : 0)));
    setStrengthScore(calculatedScore);

    if (calculatedScore === 1) setStrength('Weak');
    else if (calculatedScore === 2) setStrength('Fair');
    else if (calculatedScore === 3) setStrength('Good');
    else setStrength('Strong');
  }, [password]);

  const passwordsMatch = password === confirmPassword;
  const showMatchError = passwordTouched && confirmTouched && !passwordsMatch && confirmPassword.length > 0;

  // Recovery email validation (optional)
  const isEmailValid = (email: string) => {
    if (!email) return true; // optional, so empty is valid
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isSubmitDisabled =
    password.length < MIN_PASSWORD_LENGTH ||
    strengthScore < 2 || // Require at least Fair strength (or Good/Strong)
    !passwordsMatch ||
    (showRecoveryEmail && recoveryEmail.length > 0 && !isEmailValid(recoveryEmail)) ||
    loading;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    onSubmit(password, showRecoveryEmail && recoveryEmail ? recoveryEmail.trim() : undefined);
  };

  // Color mappings based on UI Redesign Brief
  const getStrengthColorClass = () => {
    switch (strength) {
      case 'Weak': return 'bg-red-500';
      case 'Fair': return 'bg-amber-500';
      case 'Good': return 'bg-blue-500';
      case 'Strong': return 'bg-emerald-500';
    }
  };

  const getStrengthTextClass = () => {
    switch (strength) {
      case 'Weak': return 'text-red-600';
      case 'Fair': return 'text-amber-600';
      case 'Good': return 'text-blue-600';
      case 'Strong': return 'text-emerald-600';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 animate-fade-in w-full text-left">
      {/* Password Field */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-600 flex justify-between">
          <span>New Password</span>
          <span className="text-[10px] text-slate-400 font-normal">Min. 8 characters</span>
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-slate-400" />
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordTouched(true);
            }}
            onBlur={() => setPasswordTouched(true)}
            placeholder="••••••••"
            className="w-full pl-10 pr-10 py-3 text-base border border-slate-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-brand-primary/15 bg-white shadow-sm transition"
            required
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none cursor-pointer focus:ring-2 focus:ring-brand-primary"
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="h-4.5 w-4.5" /> : <Eye className="h-4.5 w-4.5" />}
          </button>
        </div>
      </div>

      {/* Confirm Password Field */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-600">Confirm New Password</label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-slate-400" />
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setConfirmTouched(true);
            }}
            onBlur={() => setConfirmTouched(true)}
            placeholder="••••••••"
            className="w-full pl-10 pr-10 py-3 text-base border border-slate-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-brand-primary/15 bg-white shadow-sm transition"
            required
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none cursor-pointer focus:ring-2 focus:ring-brand-primary"
            tabIndex={-1}
            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
          >
            {showConfirmPassword ? <EyeOff className="h-4.5 w-4.5" /> : <Eye className="h-4.5 w-4.5" />}
          </button>
        </div>
        {showMatchError && (
          <p className="text-xs text-red-500 font-semibold flex items-center gap-1 mt-1 animate-slide-down">
            <AlertCircle className="h-3.5 w-3.5" /> Passwords don't match
          </p>
        )}
      </div>

      {/* Password Strength Meter */}
      {password.length > 0 && (
        <div className="space-y-1.5 animate-fade-in">
          <div className="flex justify-between items-center text-xs">
            <span className="font-semibold text-slate-500">Password Strength:</span>
            <span className={`font-bold ${getStrengthTextClass()}`}>{strength}</span>
          </div>
          {/* Multi-segmented strength bar */}
          <div className="grid grid-cols-4 gap-1.5 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-300 ${strengthScore >= 1 ? getStrengthColorClass() : 'bg-slate-200'}`} />
            <div className={`h-full rounded-full transition-all duration-300 ${strengthScore >= 2 ? getStrengthColorClass() : 'bg-slate-200'}`} />
            <div className={`h-full rounded-full transition-all duration-300 ${strengthScore >= 3 ? getStrengthColorClass() : 'bg-slate-200'}`} />
            <div className={`h-full rounded-full transition-all duration-300 ${strengthScore >= 4 ? getStrengthColorClass() : 'bg-slate-200'}`} />
          </div>
        </div>
      )}

      {/* Recovery Email Field (Optional) */}
      {showRecoveryEmail && (
        <div className="space-y-1.5 pt-2 border-t border-slate-100 animate-fade-in">
          <label className="text-xs font-semibold text-slate-600 flex items-center justify-between">
            <span>Recovery Email</span>
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Optional</span>
          </label>
          <p className="text-[11px] text-slate-500 leading-normal mb-2">
            We'll only use this to help you regain access if you're ever locked out of your main email account. It won't be used for login or notifications.
          </p>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-slate-400" />
            <input
              type="email"
              value={recoveryEmail}
              onChange={(e) => setRecoveryEmail(e.target.value)}
              placeholder="recovery@example.com"
              className="w-full pl-10 pr-4 py-3 text-base border border-slate-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-brand-primary/15 bg-white shadow-sm transition"
              disabled={loading}
            />
          </div>
          {recoveryEmail.length > 0 && !isEmailValid(recoveryEmail) && (
            <p className="text-xs text-red-500 font-semibold flex items-center gap-1 mt-1 animate-slide-down">
              <AlertCircle className="h-3.5 w-3.5" /> Please enter a valid email format
            </p>
          )}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitDisabled}
        className="w-full py-3.5 text-white rounded-xl text-sm font-semibold transition-all flex items-center justify-center space-x-2 cursor-pointer active:scale-[0.98] hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40 shadow-md focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
        style={{ backgroundColor: 'var(--brand-primary, #2563eb)' }}
      >
        {loading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-200 border-t-white" />
        ) : (
          <>
            <ShieldCheck className="h-4.5 w-4.5" />
            <span>{submitLabel}</span>
          </>
        )}
      </button>
    </form>
  );
}
